import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import AppointmentButton from './appointments/AppointmentButton';
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  TextField,
  Chip,
  IconButton,
  CircularProgress,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  Paper,
  Divider,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  Snackbar,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel as MuiFormControlLabel,
  Checkbox,
  Button,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
  Tooltip,
  Collapse,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  CalendarToday,
  AccessTime,
  LocationOn,
  People,
  SquareFoot,
  Star,
  CheckCircle,
  RadioButtonUnchecked,
  ExpandMore,
  ExpandLess,
  Restaurant,
  LocalBar,
  Fastfood,
  Timer,
  Info,
  Close,
  Add,
  Remove,
  Event,
  Business,
  EventSeat,
  AttachMoney,
  PictureAsPdf,
  Description,
  ArrowForwardIos,
  Check,
  ErrorOutline,
  PersonOutline,
  NearMe,
  InfoOutlined,
  HourglassEmpty,
  AddCircleOutline,
  RemoveCircleOutline,
  Warning,
} from "@mui/icons-material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";

import Navbar from "../redesign/components/Navbar";
import Footer from "../redesign/components/Footer";
import {
  fetchVenueDetail,
  getEstimate,
  addVenueToCart,
  fetchAvailability,
  fetchTermsStatus,
  acceptTerms
} from "../Apiservices/Auth/servers/fetchVenueData";
import { addToCart, triggerRefresh } from "../store/cartSlice";
import config from "../config";
import "./VenueBookingScreen.css";

// ============================================
// HELPERS
// ============================================

const formatDate = (date) => {
  if (!date) return "";
  return dayjs(date).format("DD MMM YYYY");
};

const formatTime = (time) => {
  if (!time) return "";
  return dayjs(time).format("hh:mm A");
};

const formatCurrency = (amount) => {
  if (amount == null) return "₹0";
  return `₹${Math.round(amount).toLocaleString()}`;
};

const getPricingTypeName = (type) => {
  switch (type) {
    case "per_hour":
      return "Per Hour";
    case "per_day":
      return "Per Day";
    case "per_person":
      return "Per Person";
    default:
      return type || "";
  }
};

const getPriceSuffix = (type) => {
  switch (type) {
    case "per_hour":
      return "/hour";
    case "per_day":
      return "/day";
    case "per_person":
      return "/person";
    default:
      return "";
  }
};

const hasValidDiscount = (option) => {
  const now = new Date();
  return (
    option?.discountValidUpto &&
    option?.discountPrice &&
    option.discountPrice > 0 &&
    new Date(option.discountValidUpto) > now
  );
};

const getEffectivePrice = (price, discountPrice, discountValidUpto) => {
  if (price == null) return null;
  if (
    discountPrice != null &&
    discountValidUpto &&
    new Date(discountValidUpto) > new Date()
  ) {
    return discountPrice;
  }
  return price;
};

// ============================================
// MAIN COMPONENT
// ============================================

const VenueBookingScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  

  console.log("VenueBookingScreen location:", location);

  // Get params from location state or URL
  const venueId = location.state?.venueId || location.pathname.split("/").pop();
  const eventId = location.state?.eventDetails?.id || 1;
  const initialGuestCount = location.state?.initialGuestCount || 50;
  const initialSpaceId = location.state?.initialSpaceId || null;
  const eventStartDate = location.state?.eventStartDate || null;
  const eventEndDate = location.state?.eventEndDate || null;
  const rsvpGuestCount = location.state?.rsvpGuestCount || null;
  const latitude = location.state?.latitude || null;
  const longitude = location.state?.longitude || null;

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [venue, setVenue] = useState(null);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [selectedPricingOption, setSelectedPricingOption] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs().add(1, "day"));
  const [startTime, setStartTime] = useState(dayjs().hour(9).minute(0));
  const [endTime, setEndTime] = useState(dayjs().hour(18).minute(0));
  const [guestCount, setGuestCount] = useState(initialGuestCount);
  const [guestCountFromRsvp, setGuestCountFromRsvp] = useState(false);
  const [foodSelections, setFoodSelections] = useState({});
  const [drinkSelections, setDrinkSelections] = useState({});
  const [venueTermsAccepted, setVenueTermsAccepted] = useState(false);
  const [spaceTermsAccepted, setSpaceTermsAccepted] = useState(false);
  const [isLoadingTerms, setIsLoadingTerms] = useState(true);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [isLoadingEstimate, setIsLoadingEstimate] = useState(false);
  const [stepErrors, setStepErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [availability, setAvailability] = useState([]);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);

  // Refs
  const guestCountInputRef = useRef(null);

  // ============================================
  // API CALLS
  // ============================================

  const fetchVenueDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchVenueDetail(venueId, latitude, longitude);

      if (data) {
        setVenue(data);

        // Set initial dates from event if available
        if (eventStartDate) {
          const start = dayjs(eventStartDate);
          setStartDate(start);
          if (eventEndDate) {
            setEndDate(dayjs(eventEndDate));
          } else {
            setEndDate(start.add(1, "day"));
          }
        }

        // Auto-select initial space if provided
        if (initialSpaceId && data.spaces) {
          const preselected = data.spaces.find(
            s => s.id === initialSpaceId
          );
          if (preselected) {
            setSelectedSpace(preselected);
            setGuestCount(Math.max(preselected.minCapacity || 50, guestCount));
          }
        }

        // Check terms status
        await checkTermsStatus(data.id);

        // Fetch availability for the first space if available
        if (data.spaces && data.spaces.length > 0) {
          await fetchAvailabilityForSpace(data.spaces[0].id);
        }
      } else {
        setError("Failed to load venue details");
      }
    } catch (err) {
      console.error("Error fetching venue:", err);
      setError(err.message || "Failed to load venue");
    } finally {
      setLoading(false);
    }
  };

  const checkTermsStatus = async (venueId) => {
    setIsLoadingTerms(true);
    try {
      const data = await fetchTermsStatus(venueId, eventId);
      setVenueTermsAccepted(data.venueTermsAccepted || false);
      setSpaceTermsAccepted(data.spaceTermsAccepted || false);
    } catch (err) {
      console.error("Error checking terms:", err);
      setVenueTermsAccepted(false);
      setSpaceTermsAccepted(false);
    } finally {
      setIsLoadingTerms(false);
    }
  };

  const fetchAvailabilityForSpace = async (spaceId) => {
    setIsLoadingAvailability(true);
    try {
      const slots = await fetchAvailability(venueId, spaceId, startDate.format("YYYY-MM"));
      setAvailability(slots || []);
    } catch (err) {
      console.error("Error fetching availability:", err);
      setAvailability([]);
    } finally {
      setIsLoadingAvailability(false);
    }
  };

  const acceptVenueTerms = async () => {
    setIsLoadingTerms(true);
    try {
      const response = await acceptTerms({
        myEventId: eventId,
        venueId: venue.id,
      });

      if (response.success) {
        setVenueTermsAccepted(true);
        setStepErrors(prev => ({ ...prev, step1: null }));
        setSnackbar({
          open: true,
          message: "Venue terms accepted successfully",
          severity: "success",
        });
      } else {
        throw new Error(response.message || "Failed to accept terms");
      }
    } catch (err) {
      setStepErrors(prev => ({ ...prev, step1: "Failed to accept venue terms. Please try again." }));
      setSnackbar({
        open: true,
        message: err.message || "Error accepting terms",
        severity: "error",
      });
    } finally {
      setIsLoadingTerms(false);
    }
  };

  // Update the acceptSpaceTerms function
  const acceptSpaceTerms = async () => {
    if (!selectedSpace) return;
    setIsLoadingTerms(true);
    try {
      const response = await acceptTerms({
        myEventId: eventId,
        venueId: venue.id,
        spaceId: selectedSpace.id,
      });

      if (response.success) {
        setSpaceTermsAccepted(true);
        setStepErrors(prev => ({ ...prev, step1: null }));
        setSnackbar({
          open: true,
          message: "Space terms accepted successfully",
          severity: "success",
        });
      } else {
        throw new Error(response.message || "Failed to accept terms");
      }
    } catch (err) {
      setStepErrors(prev => ({ ...prev, step1: "Failed to accept space terms. Please try again." }));
      setSnackbar({
        open: true,
        message: err.message || "Error accepting terms",
        severity: "error",
      });
    } finally {
      setIsLoadingTerms(false);
    }
  };

  const calculateEstimate = async () => {
    if (!selectedSpace || !selectedPricingOption) return;

    // Check if dates are valid
    if (startDate.isAfter(endDate)) {
      setStepErrors(prev => ({ ...prev, step2: "Start date must be before end date" }));
      return;
    }

    setIsLoadingEstimate(true);
    try {
      const fromDateStr = startDate.format("YYYY-MM-DD");
      const toDateStr = endDate.format("YYYY-MM-DD");
      const fromTimeStr = startTime.format("HH:mm");
      const toTimeStr = endTime.format("HH:mm");

      const payload = {
        venueId: venue.id,
        spaceId: selectedSpace.id,
        pricingType: selectedPricingOption.type,
        noOfGuests: guestCount,
        fromDate: fromDateStr,
        fromTime: fromTimeStr,
        toDate: toDateStr,
        toTime: toTimeStr,
      };

      // Add optional fields for per_person
      if (selectedPricingOption.type === "per_person") {
        if (selectedPricingOption.foodIncluded !== undefined) {
          payload.foodIncluded = selectedPricingOption.foodIncluded;
        }
        if (selectedPricingOption.outsideFoodAllowed !== undefined) {
          payload.outsideFoodAllowed = selectedPricingOption.outsideFoodAllowed;
        }
        if (Object.keys(foodSelections).length > 0) {
          payload.foodSelections = Object.entries(foodSelections).map(([id, count]) => ({
            menuId: parseInt(id),
            guestCount: count,
          }));
        }
        if (Object.keys(drinkSelections).length > 0) {
          payload.drinkSelections = Object.entries(drinkSelections).map(([id, count]) => ({
            drinkOptionId: parseInt(id),
            guestCount: count,
          }));
        }
      }

      const response = await getEstimate(payload);


      console.log("Estimate response:", response);

      if (response.success) {
        setEstimate(response.estimate);
        setStepErrors(prev => ({ ...prev, step2: null }));
      } else {
        setStepErrors(prev => ({ ...prev, step2: response.message || "Unable to calculate estimate" }));
        setEstimate(null);
      }
    } catch (err) {
      setStepErrors(prev => ({ ...prev, step2: err.message || "Unable to calculate estimate" }));
      setEstimate(null);
    } finally {
      setIsLoadingEstimate(false);
    }
  };

  const addToCartHandler = async () => {
    if (!selectedSpace || !selectedPricingOption) {
      setStepErrors(prev => ({ ...prev, step0: "Please select a space and pricing type." }));
      setSnackbar({
        open: true,
        message: "Please select a space and pricing type",
        severity: "warning",
      });
      return;
    }

    if (!venueTermsAccepted) {
      setStepErrors(prev => ({ ...prev, step1: "Please accept venue policy to continue." }));
      setSnackbar({
        open: true,
        message: "Please accept venue terms first",
        severity: "warning",
      });
      setCurrentStep(1);
      return;
    }

    if (!spaceTermsAccepted) {
      setStepErrors(prev => ({ ...prev, step1: "Please accept space policy to continue." }));
      setSnackbar({
        open: true,
        message: "Please accept space terms first",
        severity: "warning",
      });
      setCurrentStep(1);
      return;
    }

    // Validate food selections for per_person pricing
    if (selectedPricingOption.type === "per_person" && selectedPricingOption.foodIncluded) {
      const totalFoodGuests = Object.values(foodSelections).reduce((a, b) => a + b, 0);
      if (totalFoodGuests !== guestCount) {
        setStepErrors(prev => ({ ...prev, step2: "Please assign all guests to food options." }));
        setSnackbar({
          open: true,
          message: "Please assign all guests to food options",
          severity: "warning",
        });
        return;
      }
    }

    setIsCheckingOut(true);
    try {
      const fromDateStr = startDate.format("YYYY-MM-DD");
      const toDateStr = endDate.format("YYYY-MM-DD");
      const fromTimeStr = startTime.format("HH:mm");
      const toTimeStr = endTime.format("HH:mm");

      const payload = {
        eventId: eventId,
        venueId: venue.id,
        spaceId: selectedSpace.id,
        pricingType: selectedPricingOption.type,
        noOfGuests: guestCount,
        fromDate: fromDateStr,
        fromTime: fromTimeStr,
        toDate: toDateStr,
        toTime: toTimeStr,
      };

      // Add optional fields for per_person
      if (selectedPricingOption.type === "per_person") {
        if (selectedPricingOption.foodIncluded !== undefined) {
          payload.foodIncluded = selectedPricingOption.foodIncluded;
        }
        if (selectedPricingOption.outsideFoodAllowed !== undefined) {
          payload.outsideFoodAllowed = selectedPricingOption.outsideFoodAllowed;
        }
        if (Object.keys(foodSelections).length > 0) {
          payload.foodSelections = Object.entries(foodSelections).map(([id, count]) => ({
            menuId: parseInt(id),
            guestCount: count,
          }));
        }
        if (Object.keys(drinkSelections).length > 0) {
          payload.drinkSelections = Object.entries(drinkSelections).map(([id, count]) => ({
            drinkOptionId: parseInt(id),
            guestCount: count,
          }));
        }
      }

      const response = await addVenueToCart(payload);

      if (response.success) {
        // Dispatch to Redux
        dispatch(addToCart({
          serviceType: "venue",
          eventID: eventId,
          providerID: venue.id,
          spaceId: selectedSpace.id,
          spaceName: selectedSpace.name,
          guestCount: guestCount,
          bookingDate: fromDateStr,
          bookingTime: fromTimeStr,
          price: estimate?.subtotal,
          discountprice: estimate?.grandTotal,
          venueDetails: {
            name: venue.name,
            address: venue.fullAddress || venue.shortAddress,
            spaceName: selectedSpace.name,
            pricingType: selectedPricingOption.type,
            numberOfDays: estimate?.numberOfDays,
          },
        }));
        dispatch(triggerRefresh());

        setSnackbar({
          open: true,
          message: "Venue added to cart successfully!",
          severity: "success",
        });

        setTimeout(() => {
          navigate("/cart");
        }, 1500);
      } else {
        throw new Error(response.message || "Failed to add to cart");
      }
    } catch (err) {
      setStepErrors(prev => ({ ...prev, step2: err.message || "Failed to add to cart" }));
      setSnackbar({
        open: true,
        message: err.message || "Failed to add to cart",
        severity: "error",
      });
    } finally {
      setIsCheckingOut(false);
    }
  };

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    if (venueId) {
      fetchVenueDetails();
    }
  }, [venueId]);

  useEffect(() => {
    // Initialize guest count from props
    if (initialGuestCount > 0) {
      setGuestCount(initialGuestCount);
    } else if (rsvpGuestCount > 0) {
      setGuestCount(rsvpGuestCount);
      setGuestCountFromRsvp(true);
    }
  }, [initialGuestCount, rsvpGuestCount]);

  useEffect(() => {
    // Calculate estimate when selections change
    if (selectedSpace && selectedPricingOption) {
      const debounceTimer = setTimeout(() => {
        calculateEstimate();
      }, 500);
      return () => clearTimeout(debounceTimer);
    }
  }, [selectedSpace, selectedPricingOption, guestCount, startDate, endDate, startTime, endTime, foodSelections, drinkSelections]);

  useEffect(() => {
    // Fetch availability when space or date changes
    if (selectedSpace) {
      fetchAvailabilityForSpace(selectedSpace.id);
    }
  }, [selectedSpace, startDate]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleSpaceSelect = (space) => {
    setSelectedSpace(space);
    setSelectedPricingOption(null);
    setSpaceTermsAccepted(false);
    setStepErrors(prev => ({ ...prev, step0: null }));
    setFoodSelections({});
    setDrinkSelections({});
    setEstimate(null);
    setGuestCount(Math.max(space.minCapacity || 50, guestCount));
  };

  const handlePricingSelect = (option) => {
    setSelectedPricingOption(option);
    setStepErrors(prev => ({ ...prev, step0: null }));
    setFoodSelections({});
    setDrinkSelections({});
    setEstimate(null);
  };

  const updateGuestCount = (newCount) => {
    const clampedCount = Math.max(
      selectedSpace?.minCapacity || 1,
      Math.min(newCount, selectedSpace?.maxCapacity || 1000)
    );
    setGuestCount(clampedCount);
  };

  const updateFoodSelection = (menuId, count) => {
    const clampedCount = Math.max(0, Math.min(count, guestCount));
    setFoodSelections(prev => {
      if (clampedCount > 0) {
        return { ...prev, [menuId]: clampedCount };
      } else {
        const newSelections = { ...prev };
        delete newSelections[menuId];
        return newSelections;
      }
    });
  };

  const updateDrinkSelection = (drinkId, count) => {
    const clampedCount = Math.max(0, Math.min(count, guestCount));
    setDrinkSelections(prev => {
      if (clampedCount > 0) {
        return { ...prev, [drinkId]: clampedCount };
      } else {
        const newSelections = { ...prev };
        delete newSelections[drinkId];
        return newSelections;
      }
    });
  };

  const getTotalFoodGuests = () => {
    return Object.values(foodSelections).reduce((a, b) => a + b, 0);
  };

  const isFoodComplete = () => {
    if (!selectedPricingOption?.foodIncluded) return true;
    return getTotalFoodGuests() === guestCount;
  };

  const getNumberOfDays = () => {
    return endDate.diff(startDate, "day") + 1;
  };

  const isEstimateValid = () => {
    return estimate && estimate.grandTotal > 0;
  };

  const isTermsStepValid = () => {
    return venueTermsAccepted && spaceTermsAccepted;
  };

  const canProceedStep = (step) => {
    if (step === 0) {
      return selectedSpace !== null && selectedPricingOption !== null;
    }
    if (step === 1) {
      return isEstimateValid() && !isLoadingEstimate;
    }
    if (step === 2) {
      return isTermsStepValid();
    }
    return false;
  };

  const isSpaceAvailable = (spaceId, date) => {
    if (!availability || availability.length === 0) return true;
    const dateStr = date.format("YYYY-MM-DD");
    const isBlocked = availability.some(slot =>
      slot.spaceId === spaceId &&
      slot.date === dateStr &&
      slot.isBlocked
    );
    return !isBlocked;
  };

  // ============================================
  // RENDER HELPERS
  // ============================================

  const renderSelectSpaceStep = () => (
    <div className="step-content">
      {stepErrors.step0 && (
        <Alert severity="error" className="step-error">
          {stepErrors.step0}
        </Alert>
      )}

      {/* Venue Summary */}
      <Card className="venue-summary-card" variant="outlined">
        <CardContent>
          <Typography variant="h6" className="venue-name">
            {venue?.name}
          </Typography>
          <div className="venue-address-row">
            <LocationOn className="address-icon" />
            <Typography variant="body2" color="textSecondary">
              {venue?.shortAddress || venue?.fullAddress}
            </Typography>
          </div>
          {venue?.distanceInMeter && (
            <div className="venue-distance-row">
              <NearMe className="distance-icon" />
              <Typography variant="body2" color="textSecondary">
                {(venue.distanceInMeter / 1000).toFixed(1)} km away
              </Typography>
            </div>
          )}
          {venue?.rating && (
            <div className="venue-rating-row">
              <Star className="star-icon" />
              <Typography variant="body2" color="textSecondary">
                {venue.rating.toFixed(1)}
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>

      <Divider className="step-divider" />

      <Typography variant="h6" className="section-title">
        {initialSpaceId && selectedSpace ? "Selected Package Option" : "Choose Package Option"}
      </Typography>

      <div className="space-list">
        {venue?.spaces?.map((space) => {
          const isSelected = selectedSpace?.id === space.id;
          const isAvailable = isSpaceAvailable(space.id, startDate);

          return (
            <div
              key={space.id}
              className={`space-card ${isSelected ? "selected" : ""} ${!isAvailable ? "unavailable" : ""}`}
              onClick={() => isAvailable && handleSpaceSelect(space)}
            >
              <div className="space-card-header">
                <div className="space-selector">
                  {isSelected ? (
                    <CheckCircle className="radio-selected" />
                  ) : (
                    <RadioButtonUnchecked className="radio-unselected" />
                  )}
                  <span className="space-name">{space.name}</span>
                </div>
                <div className="space-badges">
                  {space.isRecommended && (
                    <span className="recommended-badge">Recommended</span>
                  )}
                  {!isAvailable && (
                    <span className="unavailable-badge">Not Available</span>
                  )}
                </div>
              </div>
              <div className="space-details">
                <span>Capacity: {space.minCapacity} - {space.maxCapacity} guests</span>
                <span>Area: {space.areaSqft} sqft</span>
              </div>
              {space.features?.length > 0 && (
                <div className="space-features">
                  {space.features.slice(0, 3).map((f, i) => (
                    <span key={i} className="feature-tag">{f.name}</span>
                  ))}
                </div>
              )}
              {!isAvailable && (
                <div className="availability-warning">
                  <Warning className="warning-icon" />
                  <span>This space is not available on the selected date</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selectedSpace && (
        <>
          <Divider className="step-divider" />
          <Typography variant="h6" className="section-title">
            Select Pricing Type
          </Typography>
          <div className="pricing-options">
            {selectedSpace.pricing
              ?.filter(p => (p.price || p.discountPrice || 0) > 0)
              .map((option, index) => {
                const isSelected = selectedPricingOption?.type === option.type;
                const effectivePrice = getEffectivePrice(
                  option.price,
                  option.discountPrice,
                  option.discountValidUpto
                );
                const isPerPerson = option.type === "per_person";

                return (
                  <div
                    key={index}
                    className={`pricing-option-card ${isSelected ? "selected" : ""}`}
                    onClick={() => handlePricingSelect(option)}
                  >
                    <div className="pricing-option-header">
                      <div className="pricing-selector">
                        {isSelected ? (
                          <CheckCircle className="radio-selected" />
                        ) : (
                          <RadioButtonUnchecked className="radio-unselected" />
                        )}
                        <span className="pricing-type-name">
                          {getPricingTypeName(option.type)}
                        </span>
                      </div>
                      {isPerPerson && option.foodIncluded && (
                        <span className="food-included-badge">Food Included</span>
                      )}
                    </div>
                    <div className="pricing-option-price">
                      <span className="price-amount">
                        {formatCurrency(effectivePrice)}
                      </span>
                      <span className="price-suffix">
                        {getPriceSuffix(option.type)}
                      </span>
                    </div>
                    {option.discountPrice && option.discountValidUpto && (
                      <div className="pricing-discount-info">
                        <span className="original-price">
                          {formatCurrency(option.price)}
                        </span>
                        <Timer className="timer-icon" />
                        <span className="valid-till">
                          Till {formatDate(option.discountValidUpto)}
                        </span>
                      </div>
                    )}
                    {isPerPerson && option.foodIncluded && (
                      <div className="pricing-features">
                        {option.outsideFoodAllowed && (
                          <span className="feature-badge">Outside Food Allowed</span>
                        )}
                        {option.drinkOptions?.length > 0 && (
                          <span className="feature-badge">Drinks Available</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </>
      )}
    </div>
  );

  const renderBookingDetailsStep = () => (
    <div className="step-content">
      {stepErrors.step2 && (
        <Alert severity="error" className="step-error">
          {stepErrors.step2}
        </Alert>
      )}

      {/* Summary Card */}
      <Card className="summary-card" sx={{ bgcolor: "#e3f2fd" }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold">
            Selected Package
          </Typography>
          <div className="summary-row">
            <EventSeat className="summary-icon" />
            <span>Space: {selectedSpace?.name || "Not selected"}</span>
          </div>
          <div className="summary-row">
            <AttachMoney className="summary-icon" />
            <span>Pricing: {getPricingTypeName(selectedPricingOption?.type || "")}</span>
          </div>
          {selectedPricingOption?.type === "per_day" && getNumberOfDays() > 0 && (
            <div className="summary-row">
              <HourglassEmpty className="summary-icon" />
              <span>Duration: {getNumberOfDays()} day(s)</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Date & Time */}
      <Card className="date-time-card">
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Date & Time
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <div className="date-time-grid">
              <div className="date-time-item">
                <Typography variant="caption" color="textSecondary">
                  Start Date
                </Typography>
                <DatePicker
                  value={startDate}
                  onChange={(date) => {
                    if (date) {
                      setStartDate(date);
                      if (endDate.isBefore(date)) {
                        setEndDate(date);
                      }
                      // Refetch availability for new date
                      if (selectedSpace) {
                        fetchAvailabilityForSpace(selectedSpace.id);
                      }
                    }
                  }}
                  minDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                    },
                  }}
                />
              </div>
              <div className="date-time-item">
                <Typography variant="caption" color="textSecondary">
                  End Date
                </Typography>
                <DatePicker
                  value={endDate}
                  onChange={(date) => date && setEndDate(date)}
                  minDate={startDate}
                  maxDate={startDate.add(30, "day")}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                    },
                  }}
                />
              </div>
              <div className="date-time-item">
                <Typography variant="caption" color="textSecondary">
                  Start Time
                </Typography>
                <TimePicker
                  value={startTime}
                  onChange={(time) => time && setStartTime(time)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                    },
                  }}
                />
              </div>
              <div className="date-time-item">
                <Typography variant="caption" color="textSecondary">
                  End Time
                </Typography>
                <TimePicker
                  value={endTime}
                  onChange={(time) => time && setEndTime(time)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                    },
                  }}
                />
              </div>
            </div>
          </LocalizationProvider>

          {startDate.isBefore(endDate) && (
            <div className="days-badge">
              <HourglassEmpty className="days-icon" />
              <span>{getNumberOfDays()} day{getNumberOfDays() > 1 ? "s" : ""} booking</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Guest Count */}
      <Card className="guest-count-card">
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Guest Count
          </Typography>
          <div className="guest-count-control">
            <IconButton
              onClick={() => updateGuestCount(guestCount - 1)}
              disabled={guestCount <= (selectedSpace?.minCapacity || 1)}
              className="guest-btn"
            >
              <RemoveCircleOutline />
            </IconButton>
            <TextField
              inputRef={guestCountInputRef}
              type="number"
              value={guestCount}
              onChange={(e) => updateGuestCount(parseInt(e.target.value) || 0)}
              inputProps={{ min: selectedSpace?.minCapacity || 1, max: selectedSpace?.maxCapacity || 1000 }}
              className="guest-input"
              size="small"
            />
            <IconButton
              onClick={() => updateGuestCount(guestCount + 1)}
              disabled={guestCount >= (selectedSpace?.maxCapacity || 1000)}
              className="guest-btn"
            >
              <AddCircleOutline />
            </IconButton>
          </div>
          {guestCountFromRsvp && (
            <div className="rsvp-hint">
              <People className="rsvp-icon" />
              <span>Pre-filled from RSVP responses ({rsvpGuestCount} confirmed)</span>
            </div>
          )}
          {selectedSpace && (
            <div className="capacity-hint">
              <InfoOutlined className="capacity-icon" />
              <span>Capacity: {selectedSpace.minCapacity} - {selectedSpace.maxCapacity} guests</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Food Selection */}
      {selectedPricingOption?.type === "per_person" && selectedPricingOption.foodIncluded && selectedPricingOption.menuMappings?.length > 0 && (
        <Card className="food-selection-card">
          <CardContent>
            <div className="food-header">
              <Typography variant="h6" fontWeight="bold">
                Food Selection
              </Typography>
              <span className="included-badge">Included</span>
            </div>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Select food options for your guests (must total {guestCount} guests)
            </Typography>
            <div className="food-list">
              {selectedPricingOption.menuMappings.map(({ menu }) => (
                <div key={menu.id} className="food-item">
                  <div className="food-item-header">
                    <span className={`food-type-dot ${menu.type === "veg" ? "veg" : "non-veg"}`} />
                    <span className="food-name">{menu.name}</span>
                    {menu.description && (
                      <Tooltip title={menu.description}>
                        <IconButton size="small" className="food-info-btn">
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                  <div className="food-item-control">
                    <IconButton
                      size="small"
                      onClick={() => updateFoodSelection(menu.id, (foodSelections[menu.id] || 0) - 1)}
                      disabled={(foodSelections[menu.id] || 0) <= 0}
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                    <TextField
                      type="number"
                      value={foodSelections[menu.id] || 0}
                      onChange={(e) => updateFoodSelection(menu.id, parseInt(e.target.value) || 0)}
                      inputProps={{ min: 0, max: guestCount }}
                      size="small"
                      className="food-count-input"
                    />
                    <IconButton
                      size="small"
                      onClick={() => updateFoodSelection(menu.id, (foodSelections[menu.id] || 0) + 1)}
                      disabled={(foodSelections[menu.id] || 0) >= guestCount}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </div>
                  <Typography variant="caption" className="food-price">
                    {formatCurrency(menu.discountPrice || menu.price)}/person
                  </Typography>
                </div>
              ))}
            </div>
            <Divider />
            <div className="food-total">
              <span>Total Guests Assigned:</span>
              <span className={isFoodComplete() ? "complete" : "incomplete"}>
                {getTotalFoodGuests()} / {guestCount}
              </span>
            </div>
            {!isFoodComplete() && (
              <Alert severity="warning" className="food-warning">
                Please assign all {guestCount} guests to food options
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Drink Selection */}
      {selectedPricingOption?.type === "per_person" && selectedPricingOption.drinkOptions?.length > 0 && (
        <Card className="drink-selection-card">
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Drink Options (Optional)
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Select drink packages for your guests
            </Typography>
            <div className="drink-list">
              {selectedPricingOption.drinkOptions.map((drink) => (
                <div key={drink.id} className="drink-item">
                  <div className="drink-item-header">
                    <span className="drink-name">{drink.name}</span>
                    {drink.description && (
                      <Tooltip title={drink.description}>
                        <IconButton size="small" className="drink-info-btn">
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                  <div className="drink-item-control">
                    <IconButton
                      size="small"
                      onClick={() => updateDrinkSelection(drink.id, (drinkSelections[drink.id] || 0) - 1)}
                      disabled={(drinkSelections[drink.id] || 0) <= 0}
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                    <TextField
                      type="number"
                      value={drinkSelections[drink.id] || 0}
                      onChange={(e) => updateDrinkSelection(drink.id, parseInt(e.target.value) || 0)}
                      inputProps={{ min: 0, max: guestCount }}
                      size="small"
                      className="drink-count-input"
                    />
                    <IconButton
                      size="small"
                      onClick={() => updateDrinkSelection(drink.id, (drinkSelections[drink.id] || 0) + 1)}
                      disabled={(drinkSelections[drink.id] || 0) >= guestCount}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </div>
                  <Typography variant="caption" className="drink-price">
                    {formatCurrency(drink.discountPrice || drink.pricePerPerson)}/person
                  </Typography>
                  {drink.limitDescription && (
                    <Typography variant="caption" className="drink-limit" color="textSecondary">
                      Limit: {drink.limitDescription}
                    </Typography>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Divider />

      {/* Estimate */}
      <div className="estimate-section">
        {isLoadingEstimate ? (
          <div className="estimate-loading">
            <CircularProgress size={32} />
            <Typography variant="body2" color="textSecondary">
              Calculating estimate...
            </Typography>
          </div>
        ) : estimate ? (
          <Card className="estimate-card" sx={{ bgcolor: "#e3f2fd" }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Price Estimate
              </Typography>

              {estimate.foodBreakdown?.length > 0 && (
                <>
                  <Typography variant="subtitle2" className="estimate-subtitle">
                    Food menus
                  </Typography>
                  {estimate.foodBreakdown.map((item, i) => (
                    <div key={i} className="estimate-row">
                      <span>{item.name} ({item.guestCount} x {formatCurrency(item.pricePerPerson)})</span>
                      <span>{formatCurrency(item.subtotal)}</span>
                    </div>
                  ))}
                  {estimate.foodTotal > 0 && (
                    <div className="estimate-row bold">
                      <span>Food total</span>
                      <span>{formatCurrency(estimate.foodTotal)}</span>
                    </div>
                  )}
                </>
              )}

              {estimate.drinkBreakdown?.length > 0 && (
                <>
                  <Typography variant="subtitle2" className="estimate-subtitle">
                    Drink selections
                  </Typography>
                  {estimate.drinkBreakdown.map((item, i) => (
                    <div key={i} className="estimate-row">
                      <span>{item.name} ({item.guestCount} x {formatCurrency(item.pricePerPerson)})</span>
                      <span>{formatCurrency(item.subtotal)}</span>
                    </div>
                  ))}
                  {estimate.drinkTotal > 0 && (
                    <div className="estimate-row bold">
                      <span>Drink total</span>
                      <span>{formatCurrency(estimate.drinkTotal)}</span>
                    </div>
                  )}
                </>
              )}

              {!estimate.foodBreakdown?.length && !estimate.drinkBreakdown?.length && (
                <div className="estimate-row">
                  <span>{getPricingTypeName(selectedPricingOption?.type)} charge</span>
                  <span>{formatCurrency(estimate.basePrice || estimate.subtotal)}</span>
                </div>
              )}

              <Divider />

              <div className="estimate-row bold">
                <span>Subtotal</span>
                <span>{formatCurrency(estimate.subtotal)}</span>
              </div>
              <div className="estimate-row">
                <span>GST ({estimate.gstPercentage || 18}%)</span>
                <span>{formatCurrency(estimate.gstAmount)}</span>
              </div>

              <Divider sx={{ borderWidth: 2 }} />

              <div className="estimate-row grand-total">
                <span className="grand-total-label">Grand Total</span>
                <span className="grand-total-amount">{formatCurrency(estimate.grandTotal)}</span>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Alert severity="info">
            Select a space and pricing type to see the estimate
          </Alert>
        )}
      </div>

      {selectedSpace && (guestCount < selectedSpace.minCapacity || guestCount > selectedSpace.maxCapacity) && (
        <Alert severity="error" className="capacity-error">
          Guest count must be between {selectedSpace.minCapacity} and {selectedSpace.maxCapacity}
        </Alert>
      )}
    </div>
  );

  const renderTermsStep = () => (
    <div className="step-content">
      {stepErrors.step1 && (
        <Alert severity="error" className="step-error">
          {stepErrors.step1}
        </Alert>
      )}

      {[{ id: venue?.contractFileId, label: 'Venue contract (PDF)' },
        { id: selectedSpace?.contractFileId, label: 'Space contract (PDF)' }]
        .filter(document => document.id).map(document => (
          <Card className="terms-card" key={document.label}>
            <CardContent>
              <MuiButton component="a" href={config.API_BASE_URL + '/gallery/download/' + encodeURIComponent(document.id) + '?isDownload=false'} target="_blank" rel="noopener noreferrer" startIcon={<PictureAsPdf />}>
                {document.label}
              </MuiButton>
            </CardContent>
          </Card>
        ))}
      {/* Venue Terms */}
      <Card className="terms-card">
        <CardContent>
          <div className="terms-header">
            <Business className="terms-icon" />
            <Typography variant="subtitle1" fontWeight="bold">
              Venue Terms: {venue?.name}
            </Typography>
            {venueTermsAccepted && <CheckCircle className="terms-checked" />}
          </div>
          <div className="terms-list">
            {venue?.rules?.map((rule, i) => (
              <div key={i} className="terms-item">
                <InfoOutlined className="terms-item-icon" />
                <div>
                  <Typography variant="body2" fontWeight="500">
                    {rule.name}
                  </Typography>
                  {rule.description && (
                    <Typography variant="caption" color="textSecondary">
                      {rule.description}
                    </Typography>
                  )}
                </div>
              </div>
            ))}
            {(!venue?.rules || venue.rules.length === 0) && (
              <Typography variant="body2" color="textSecondary">
                No specific venue rules available.
              </Typography>
            )}
          </div>
          {!venueTermsAccepted ? (
            <Button
              variant="contained"
              onClick={acceptVenueTerms}
              disabled={isLoadingTerms}
              className="accept-terms-btn"
            >
              Accept Venue Terms
            </Button>
          ) : (
            <Typography variant="body2" className="terms-accepted" color="success.main">
              ✓ Terms accepted
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Space Terms */}
      {selectedSpace && (
        <Card className="terms-card">
          <CardContent>
            <div className="terms-header">
              <EventSeat className="terms-icon" />
              <Typography variant="subtitle1" fontWeight="bold">
                Space Terms: {selectedSpace.name}
              </Typography>
              {spaceTermsAccepted && <CheckCircle className="terms-checked" />}
            </div>
            <div className="terms-list">
              {selectedSpace.rules?.map((rule, i) => (
                <div key={i} className="terms-item">
                  <InfoOutlined className="terms-item-icon" />
                  <div>
                    <Typography variant="body2" fontWeight="500">
                      {rule.name}
                    </Typography>
                    {rule.description && (
                      <Typography variant="caption" color="textSecondary">
                        {rule.description}
                      </Typography>
                    )}
                  </div>
                </div>
              ))}
              {(!selectedSpace.rules || selectedSpace.rules.length === 0) && (
                <Typography variant="body2" color="textSecondary">
                  No specific terms for this space.
                </Typography>
              )}
            </div>
            {!spaceTermsAccepted ? (
              <Button
                variant="contained"
                onClick={acceptSpaceTerms}
                disabled={isLoadingTerms}
                className="accept-terms-btn"
              >
                Accept Space Terms
              </Button>
            ) : (
              <Typography variant="body2" className="terms-accepted" color="success.main">
                ✓ Terms accepted
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="venue-booking-loading">
          <CircularProgress />
          <Typography variant="body2" color="textSecondary">
            Loading venue details...
          </Typography>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !venue) {
    return (
      <>
        <Navbar />
        <div className="venue-booking-error">
          <ErrorOutline className="error-icon" />
          <Typography variant="h6" className="error-title">
            {error || "Failed to load venue"}
          </Typography>
          <Button variant="contained" onClick={fetchVenueDetails}>
            Retry
          </Button>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="venue-booking-screen">
        <AppointmentButton providerId={venue?.serviceProviderId || venue?.provider?.id} providerName={venue?.name} eventId={location.state?.eventDetails?.id || new URLSearchParams(location.search).get('eventId')} source="venue" contextId={venue?.id} />
        {/* Header */}
        <div className="venue-booking-header">
          <IconButton className="back-button" onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" className="page-title">
            {venue?.name || "Venue Booking"}
          </Typography>
        </div>

        {/* Content */}
        <div className="venue-booking-content">
          {isLoadingTerms || isCheckingOut ? (
            <div className="loading-overlay">
              <CircularProgress />
              <Typography variant="body2" color="textSecondary">
                {isCheckingOut ? "Adding to cart..." : "Loading..."}
              </Typography>
            </div>
          ) : (
            <Stepper activeStep={currentStep} orientation="vertical" className="booking-stepper">
              {/* Step 1: Select Space */}
              <Step>
                <StepLabel>Choose Package</StepLabel>
                <StepContent>
                  {renderSelectSpaceStep()}
                  <div className="step-actions">
                    <Button
                      variant="contained"
                      onClick={() => setCurrentStep(1)}
                      disabled={!canProceedStep(0)}
                      className="continue-btn"
                    >
                      Continue
                    </Button>
                  </div>
                </StepContent>
              </Step>

              {/* Step 2: Booking Details */}
              <Step>
                <StepLabel>Booking Details</StepLabel>
                <StepContent>
                  {renderBookingDetailsStep()}
                  <div className="step-actions">
                    <Button onClick={() => setCurrentStep(0)} className="back-btn">
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => setCurrentStep(2)}
                      disabled={!canProceedStep(1)}
                      className="continue-btn"
                    >
                      Continue
                    </Button>
                  </div>
                </StepContent>
              </Step>

              {/* Step 3: Accept Terms */}
              <Step>
                <StepLabel>Accept Terms</StepLabel>
                <StepContent>
                  {renderTermsStep()}
                  <div className="step-actions">
                    <Button onClick={() => setCurrentStep(1)} className="back-btn">
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={addToCartHandler}
                      disabled={!canProceedStep(2) || isCheckingOut}
                      className="add-to-cart-btn"
                    >
                      {isCheckingOut ? "Adding..." : "Add To Cart"}
                    </Button>
                  </div>
                </StepContent>
              </Step>
            </Stepper>
          )}
        </div>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          className="snackbar-alert"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </>
  );
};

export default VenueBookingScreen;
